import { ApolloError, useMutation } from "@apollo/client";
import moment from "moment";
import { useEffect, useState } from "react";
import Toggle from 'react-toggle'
import ReactTooltip from "react-tooltip";
import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
import { Flex, Heading, Card, Text, Box, Button } from "rebass/styled-components";
import styled from "styled-components";
import Modal from "styled-react-modal";
import { toast } from 'react-toastify';
import { NoUpcomings } from "../../components/icons/NoUpcomings";
import { Skeleton } from "../../components/loadingSkeleton/Skeleton";
import SendSms from "../../components/SendSms";
import { REPORT_NO_SHOW } from "../../graphql/mutations";
import { GET_PATIENT_CHART_ALERTS, GET_UPCOMINGS } from "../../graphql/queries";
import { IReportNoShowOutput, IReportNoShowInput } from "../../graphql/types/mutations/appointment";
import { IGetUpcominsOutput, IUpcoming } from "../../graphql/types/queries/appointment";
import { IUser } from "../../graphql/types/queries/user";
import useInterval from "../../hooks/useInterval";
import withSession from "../../lib/withSession";
import { Constants } from "../../utils/Constants";
import { formatPhoneNumber } from "../../utils/formatPhoneNumber";
import { getMockedFirstName, getMockedLastName, getMockedPhone } from "../../utils/mocks";
import { ToolbarMenuButton } from "../messages/components/ConversationToolbar";
import { PatientInner } from "../Patient";
import { TalkingPoints } from "./TalkingPoints";
import MinimalCalendarGrid from "./MinimalCalendarGrid";
import { SpecialModalBackground } from "../../components/Commons";
import ViewHpis from "../patient/ViewHpis";
import HpisWrapper from "../patient/HpisWrapper";


const Wrapper = styled(Flex)`
  overflow: auto;
  width: auto;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;

const VerticalTimelineElementCustom = styled(VerticalTimelineElement)`
  margin-bottom: 0 !important;
  margin-top: 12px !important;
  &:first-child {
    margin-top: 0px !important
  }
  & > .vertical-timeline-element-content {
  background-color: transparent !important;
  background: transparent !important;
  }
`

const VerticalTimelineElementCustomFilter = styled(VerticalTimelineElement)`
  background-color: transparent;
  box-shadow: initial;
  margin-bottom: 0 !important;
  margin-top: 12px !important;
  &:first-child {
    margin-top: 0px !important
  }
  & > .vertical-timeline-element-content {
  background-color: transparent !important;
  box-shadow: initial !important;
  }
`
interface UpcomingsProps {
  data: IGetUpcominsOutput | undefined,
  loading: boolean,
  error: ApolloError | undefined,
  refetch: () => void,
  selectedDate?: string | null | undefined
  hidePastSelector?: boolean
  setSelectedDate?: (date: string | null) => void
}

interface IUpcomingCardProps {
  isNewPatient?: boolean,
  name: string,
  dates: string,
  phone: string,
}

const iconStyle = {
  width: '20px',
  height: '20px',
  background: '#FFFFFF',
  border: '3px solid #BCFFEB',
  margin: '10px',
  boxShadow: 'initial'
}

const contentStyle = {
  background: '#F0F5FD',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.05)',
  borderRadius: '10px',
  padding: '0px',
}
const contentArrowStyle = {
  borderRight: '7px solid  #F0F5FD',
}

const convertMinsToHrsMins = (mins: number) => {
  let h = Math.floor(mins / 60);
  let m = mins % 60;
  let hh = h < 10 && h > 0 ? `0${h}` : h;
  if (h !== 0) {
    let mm = m < 10 && m > 0 ? `0${Math.abs(m)}` : Math.abs(m);
    return `${hh}:${mm} hours`
  } else {
    let mm = m < 10 && m > 0 ? `0${m}` : m;
    return `${mm} minutes`;
  }
}

const UpcomingCard = ({
  upcoming,
  session
}: { upcoming: IUpcoming, session: IUser }) => {
  // console.log({upcoming})
  const [isViewHpis, setIsViewHpis] = useState(false)
  const [startsIn, setStartsIn] = useState<string | null>(null)
  const [hasEnded, setHasEnded] = useState(false)
  const [isInMoreThan24, setIsInMoreThan24] = useState(false)
  const [isViewPatient, setIsViewPatient] = useState<boolean>(false);
  const [isViewTalkingPoints, setIsViewTalkingPoints] = useState<boolean>(false);
  const [isSendSms, setisSendSms] = useState<boolean>(false);
  const [canReportNoShow, setCanReportNoShow] = useState(false)
  const [reportNoShow, { loading, data, error }] = useMutation<IReportNoShowOutput, IReportNoShowInput>(REPORT_NO_SHOW, {
    refetchQueries: [{ query: GET_UPCOMINGS }, { query: GET_PATIENT_CHART_ALERTS, variables: { id: upcoming.patientId } }],
    awaitRefetchQueries: true
  })
  useEffect(() => {
    getStartsIn()
  }, [])

  useEffect(() => {
    if (!!loading) return
    if (!!data) {
      if (data.reportNoShow && data.reportNoShow.ok) {
        toast.success('No show reported', {
          autoClose: 3000
        });
      } else {
        toast.error('No show not reported', {
          autoClose: 3000
        });
      }
      return
    }
    if (!!error) {
      toast.error('No show not reported', {
        autoClose: 3000
      });
    }

  }, [data, error, loading])

  useInterval(() => {
    getStartsIn()
  }, 60000)

  const handleReportNoShow = () => {
    reportNoShow({
      variables: {
        appointmentDateTime: upcoming.date + ' ' + upcoming.startTime,
        athenaId: upcoming.patientId,
        providerName: session.patientFacingName || session.firstName + ' ' + session.lastName
      }
    })
  }

  const getStartsIn = () => {
    if (moment(upcoming.dateEnd).isBefore(moment())) {
      setCanReportNoShow(true)
      const b = moment(upcoming.dateEnd).diff(moment(), 'minutes')
      setHasEnded(true)
      setStartsIn(`Ended ${convertMinsToHrsMins(b * -1)} ago`)
      setIsInMoreThan24(b * -1 >= 1400)
    } else {
      const a = moment(upcoming.dateStart).diff(moment(), 'minutes')
      const b = moment(upcoming.dateEnd).diff(moment(), 'minutes')
      setCanReportNoShow(a < -9)
      setIsInMoreThan24(a >= 1400)
      setHasEnded(false)
      setStartsIn(`Starts in ${convertMinsToHrsMins(a)}`)
    }
  }
  if (upcoming.isSlot) {
    return null
  }
  let patientName = `${upcoming.patient.firstname?.charAt(0)}. ${upcoming.patient.lastname}`
  let fullPatientName = `${upcoming.patient.firstname?.charAt(0)}. ${upcoming.patient.lastname}`
  let homephone = upcoming.patient.homephone
  if (Constants.IS_SCRAMBLING_MODE) {
    patientName = `${getMockedFirstName().charAt(0)}. ${getMockedLastName()}`
    fullPatientName = `${getMockedFirstName()} ${getMockedLastName()}`
    homephone = getMockedPhone()
  }
  
  const getTypeAndColor = () => {
    if (!upcoming || !upcoming.appointmentTypeId) {
      return {
        title: 'hypo',
        background: 'rgba(146, 196, 255, 0.5)',
        color: '#216CBC',
      };
    }

    const id = upcoming.appointmentTypeId.toString();

    const MENO_IDS = [
      (window as any)._env_.REACT_APP_ATHENA_MENO15_APPOINTMENT_TYPE_ID,
      (window as any)._env_.REACT_APP_ATHENA_MENO30_APPOINTMENT_TYPE_ID,
      (window as any)._env_.REACT_APP_ATHENA_BRIDGE_MENO15_APPOINTMENT_TYPE_ID,
      (window as any)._env_.REACT_APP_ATHENA_BRIDGE_MENO30_APPOINTMENT_TYPE_ID,
    ].map(String);

    const WL_IDS = [
      (window as any)._env_.REACT_APP_ATHENA_WEIGHTLOSS15_APPOINTMENT_TYPE_ID,
      (window as any)._env_.REACT_APP_ATHENA_WEIGHTLOSS30_APPOINTMENT_TYPE_ID,
      (window as any)._env_.REACT_APP_ATHENA_B2B_WEIGHTLOSS15_APPOINTMENT_TYPE_ID,
      (window as any)._env_.REACT_APP_ATHENA_B2B_WEIGHTLOSS30_APPOINTMENT_TYPE_ID,
      (window as any)._env_.REACT_APP_ATHENA_BRIDGE_WEIGHTLOSS15_APPOINTMENT_TYPE_ID,
      (window as any)._env_.REACT_APP_ATHENA_BRIDGE_WEIGHTLOSS30_APPOINTMENT_TYPE_ID,
    ].map(String);

    if (MENO_IDS.includes(id)) {
      return {
        title: 'meno',
        background: 'rgba(255, 153, 187, 0.5)',
        color: '#B54D2D',
      };
    }

    if (WL_IDS.includes(id)) {
      return {
        title: 'WL',
        background: 'rgba(215, 179, 235, 0.5)',
        color: '#661A90',
      };
    }

    return {
      title: 'hypo',
      background: 'rgba(146, 196, 255, 0.5)',
      color: '#216CBC',
    };
  }
  return (
    <Flex width='100%'>

      <Card style={{ flex: 'initial', padding: '12px 12px', position: 'relative', overflow: 'hidden', width: '100%' }}>
        <Flex flexDirection={'column'}>
          <Flex mb={3} justifyContent={'space-between'}>
            {!upcoming.patient.firstappointment ? (
              <Box style={{ background: 'rgba(184, 214, 245, 0.29)', borderRadius: '10px', padding: '3px 12px', alignSelf: 'flex-start' }}>
                <Text color={'rgba(52, 79, 122, 0.6)'} fontSize='12px'>New Patient</Text>
              </Box>
            ) : (
              <Box style={{ background: 'rgba(184, 245, 189, 0.29)', borderRadius: '10px', padding: '3px 12px', alignSelf: 'flex-start' }}>
                <Text color={'rgba(52, 122, 58, 0.6)'} fontSize='12px'>Returning Patient</Text>
              </Box>
            )}
            <Box style={{ background: getTypeAndColor()?.background, borderRadius: '10px', padding: '3px 12px', alignSelf: 'flex-start' }}>
              <Text color={getTypeAndColor()?.color} fontSize='12px'>{getTypeAndColor()?.title}</Text>
            </Box>
          </Flex>

          <Text fontWeight={600} fontSize='18px' mb={2}>{patientName}</Text>
          <Flex mb={3} alignItems={'center'}>

            <Text fontWeight={400} fontSize='11px' color='rgba(52, 79, 122, 0.6)' mr={2}>{`${moment(upcoming.dateStart).format('hh:mmA')} - ${moment(upcoming.dateEnd).format('hh:mmA')}`}</Text>
            {!!isInMoreThan24 && (
              <>
                <Box style={{ border: `1px solid #344f79`, borderRadius: '8px', padding: '0 4px', background: 'rgb(121,146, 179)' }}>
                  <Text fontWeight={400} fontSize='12px' color='white'>{`${upcoming.patient.state}`}</Text>
                </Box>

                <svg
                  data-for='tooltipGenerateHpi' data-tip='Copy HPI to clipboard'
                  onClick={() => {
                    setIsViewHpis(true)
                  }} style={{ marginRight: '12px', marginLeft: '12px', cursor: 'pointer' }} width="18" height="24" viewBox="0 0 18 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_525_2)">
                    <path d="M15.75 3H12C12 1.34531 10.6547 0 9 0C7.34531 0 6 1.34531 6 3H2.25C1.00781 3 0 4.00781 0 5.25V21.75C0 22.9922 1.00781 24 2.25 24H15.75C16.9922 24 18 22.9922 18 21.75V5.25C18 4.00781 16.9922 3 15.75 3ZM9 1.875C9.62344 1.875 10.125 2.37656 10.125 3C10.125 3.62344 9.62344 4.125 9 4.125C8.37656 4.125 7.875 3.62344 7.875 3C7.875 2.37656 8.37656 1.875 9 1.875ZM13.5 16.125C13.5 16.3313 13.3313 16.5 13.125 16.5H10.5V19.125C10.5 19.3313 10.3313 19.5 10.125 19.5H7.875C7.66875 19.5 7.5 19.3313 7.5 19.125V16.5H4.875C4.66875 16.5 4.5 16.3313 4.5 16.125V13.875C4.5 13.6687 4.66875 13.5 4.875 13.5H7.5V10.875C7.5 10.6687 7.66875 10.5 7.875 10.5H10.125C10.3313 10.5 10.5 10.6687 10.5 10.875V13.5H13.125C13.3313 13.5 13.5 13.6687 13.5 13.875V16.125ZM13.5 7.125C13.5 7.33125 13.3313 7.5 13.125 7.5H4.875C4.66875 7.5 4.5 7.33125 4.5 7.125V6.375C4.5 6.16875 4.66875 6 4.875 6H13.125C13.3313 6 13.5 6.16875 13.5 6.375V7.125Z" fill="#344F7A" />
                  </g>
                  <defs>
                    <clipPath id="clip0_525_2">
                      <rect width="18" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </>
            )}

            {!isInMoreThan24 && (
              <>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 1.125C4.65117 1.125 1.125 4.65117 1.125 9C1.125 13.3488 4.65117 16.875 9 16.875C13.3488 16.875 16.875 13.3488 16.875 9C16.875 4.65117 13.3488 1.125 9 1.125ZM12.1025 11.4205L11.5998 12.1061C11.5889 12.121 11.5751 12.1336 11.5593 12.1432C11.5434 12.1528 11.5259 12.1591 11.5076 12.1619C11.4893 12.1647 11.4706 12.1638 11.4527 12.1594C11.4347 12.1549 11.4178 12.1469 11.4029 12.1359L8.49551 10.016C8.4774 10.003 8.46267 9.98584 8.45258 9.96596C8.44248 9.94607 8.43731 9.92406 8.4375 9.90176V5.0625C8.4375 4.98516 8.50078 4.92188 8.57812 4.92188H9.42363C9.50098 4.92188 9.56426 4.98516 9.56426 5.0625V9.41309L12.0709 11.2254C12.1342 11.2693 12.1482 11.3572 12.1025 11.4205Z" fill="#F8A294" />
                </svg>

                <Text fontWeight={400} fontSize='11px' color='#F8A294' ml={2}>{startsIn}</Text>
              </>
            )}
          </Flex>
          {!isInMoreThan24 && (
            <Flex mb={3} alignItems={'center'}>
              <Box mr='12px' style={{ border: `1px solid #344f79`, borderRadius: '8px', padding: '0 4px', background: 'rgb(121,146, 179)' }}>
                <Text fontWeight={400} fontSize='12px' color='white'>{`${upcoming.patient.state}`}</Text>
              </Box>

              <svg
                data-for='tooltipGenerateHpi' data-tip='Copy HPI to clipboard'
                onClick={() => {
                  setIsViewHpis(true)
                }} style={{ marginRight: '12px', cursor: 'pointer' }} width="18" height="24" viewBox="0 0 18 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_525_2)">
                  <path d="M15.75 3H12C12 1.34531 10.6547 0 9 0C7.34531 0 6 1.34531 6 3H2.25C1.00781 3 0 4.00781 0 5.25V21.75C0 22.9922 1.00781 24 2.25 24H15.75C16.9922 24 18 22.9922 18 21.75V5.25C18 4.00781 16.9922 3 15.75 3ZM9 1.875C9.62344 1.875 10.125 2.37656 10.125 3C10.125 3.62344 9.62344 4.125 9 4.125C8.37656 4.125 7.875 3.62344 7.875 3C7.875 2.37656 8.37656 1.875 9 1.875ZM13.5 16.125C13.5 16.3313 13.3313 16.5 13.125 16.5H10.5V19.125C10.5 19.3313 10.3313 19.5 10.125 19.5H7.875C7.66875 19.5 7.5 19.3313 7.5 19.125V16.5H4.875C4.66875 16.5 4.5 16.3313 4.5 16.125V13.875C4.5 13.6687 4.66875 13.5 4.875 13.5H7.5V10.875C7.5 10.6687 7.66875 10.5 7.875 10.5H10.125C10.3313 10.5 10.5 10.6687 10.5 10.875V13.5H13.125C13.3313 13.5 13.5 13.6687 13.5 13.875V16.125ZM13.5 7.125C13.5 7.33125 13.3313 7.5 13.125 7.5H4.875C4.66875 7.5 4.5 7.33125 4.5 7.125V6.375C4.5 6.16875 4.66875 6 4.875 6H13.125C13.3313 6 13.5 6.16875 13.5 6.375V7.125Z" fill="#344F7A" />
                </g>
                <defs>
                  <clipPath id="clip0_525_2">
                    <rect width="18" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </Flex>
          )}
          <Flex width={'100%'} mb={3} justifyContent='center'>
            <Flex flex={1} alignItems='center'>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.1078 3.14727L10.6518 1.69258C10.549 1.58942 10.427 1.50756 10.2925 1.45172C10.1581 1.39588 10.0139 1.36715 9.86836 1.36719C9.57168 1.36719 9.29278 1.4834 9.0836 1.69258L7.5168 3.25937C7.41364 3.36209 7.33178 3.48417 7.27594 3.61861C7.2201 3.75305 7.19137 3.8972 7.19141 4.04277C7.19141 4.33945 7.30762 4.61836 7.5168 4.82754L8.6625 5.97324C8.39432 6.56436 8.02146 7.10211 7.56192 7.56055C7.10353 8.02121 6.56582 8.39542 5.97461 8.66523L4.82891 7.51953C4.7262 7.41637 4.60411 7.33452 4.46967 7.27867C4.33523 7.22283 4.19108 7.19411 4.04551 7.19414C3.74883 7.19414 3.46992 7.31035 3.26074 7.51953L1.69258 9.08496C1.58929 9.18786 1.50737 9.31017 1.45152 9.44485C1.39568 9.57953 1.36702 9.72393 1.36719 9.86973C1.36719 10.1664 1.4834 10.4453 1.69258 10.6545L3.1459 12.1078C3.47949 12.4428 3.94024 12.6328 4.41328 12.6328C4.51309 12.6328 4.60879 12.6246 4.70313 12.6082C6.5461 12.3047 8.37403 11.3244 9.84922 9.85059C11.3231 8.37813 12.302 6.55156 12.6096 4.70312C12.7025 4.13848 12.5152 3.55742 12.1078 3.14727Z" fill="#344F7A" fill-opacity="0.65" />
              </svg>

              <Text fontWeight={400} color='rgba(52, 79, 122, 0.6)' fontSize='11px' ml={2}>{homephone ? formatPhoneNumber(homephone) : 'Not found'}</Text>

            </Flex>
            {!isInMoreThan24 && (
              <Box style={{ border: '1px solid #CDD9EC', cursor: 'pointer', padding: '4px 12px', borderRadius: '10px' }} mr={'6px'} onClick={() => setisSendSms(true)}>
                <Text fontSize={'11px'} textAlign={'center'}>Send SMS</Text>
              </Box>
            )}
          </Flex>

          {!!canReportNoShow && (
            <Button variant={'pink'} style={{
              padding: '6px 0',
              fontSize: '12px',
              borderRadius: '10px'
            }} disabled={!!loading} mr={'6px'} mb={3} onClick={() => {
              if (!!loading) return
              handleReportNoShow()
            }}>Report No Show</Button>
            // <Box style={{ border: '1px solid #CDD9EC', cursor: 'pointer', padding: '4px 12px', borderRadius: '10px' }} mb={3} onClick={() => {
            //   if (!!loading) return
            //   handleReportNoShow()
            // }}>
            //   <Text fontSize={'11px'} textAlign={'center'}>Report No Show</Text>
            // </Box>
          )}
          <Flex>
            <Box style={{ border: '1px solid #CDD9EC', cursor: 'pointer', padding: '4px 12px', flex: 1, borderRadius: '10px' }} mr={'6px'} onClick={() => setIsViewPatient(true)}>
              <Text fontSize={'11px'} textAlign={'center'}>View Patient</Text>
            </Box>
            {!isInMoreThan24 && (
              <Box style={{ background: '#344F7A', cursor: 'pointer', padding: '4px 12px', flex: 1, borderRadius: '10px' }} ml={'6px'} onClick={() => {
                window.open(session.providerRoom?.startsWith('http') ? session.providerRoom : `https://${session.providerRoom}`, "_blank");
              }}>
                <Text fontSize={'11px'} color='white' textAlign={'center'}>Start Consult</Text>
              </Box>)}
          </Flex>

          <Text fontSize={'12px'} style={{ color: '#0645AD', cursor: 'pointer' }} mt={2} onClick={() => { setIsViewTalkingPoints(true) }} textAlign={'left'} fontWeight={500}>Review Talking Points {'>>'}</Text>

        </Flex>
        {upcoming.isMobileUser && (
          <>
            <Box data-tip='Mobile User' style={{ position: "absolute", top: -70, right: -50, width: '150px', height: '80px', background: '#7992B3', transform: 'rotate(45deg)' }} />
            <svg style={{ position: 'absolute', top: 6, right: 4 }} width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_278_158)">
                <path d="M14.4375 1.3125C14.7856 1.3125 15.1194 1.45078 15.3656 1.69692C15.6117 1.94306 15.75 2.2769 15.75 2.625V18.375C15.75 18.7231 15.6117 19.0569 15.3656 19.3031C15.1194 19.5492 14.7856 19.6875 14.4375 19.6875H6.5625C6.2144 19.6875 5.88056 19.5492 5.63442 19.3031C5.38828 19.0569 5.25 18.7231 5.25 18.375V2.625C5.25 2.2769 5.38828 1.94306 5.63442 1.69692C5.88056 1.45078 6.2144 1.3125 6.5625 1.3125H14.4375ZM6.5625 0C5.86631 0 5.19863 0.276562 4.70634 0.768845C4.21406 1.26113 3.9375 1.92881 3.9375 2.625V18.375C3.9375 19.0712 4.21406 19.7389 4.70634 20.2312C5.19863 20.7234 5.86631 21 6.5625 21H14.4375C15.1337 21 15.8014 20.7234 16.2937 20.2312C16.7859 19.7389 17.0625 19.0712 17.0625 18.375V2.625C17.0625 1.92881 16.7859 1.26113 16.2937 0.768845C15.8014 0.276562 15.1337 0 14.4375 0L6.5625 0Z" fill="white" />
                <path d="M10.5 18.375C10.8481 18.375 11.1819 18.2367 11.4281 17.9906C11.6742 17.7444 11.8125 17.4106 11.8125 17.0625C11.8125 16.7144 11.6742 16.3806 11.4281 16.1344C11.1819 15.8883 10.8481 15.75 10.5 15.75C10.1519 15.75 9.81806 15.8883 9.57192 16.1344C9.32578 16.3806 9.1875 16.7144 9.1875 17.0625C9.1875 17.4106 9.32578 17.7444 9.57192 17.9906C9.81806 18.2367 10.1519 18.375 10.5 18.375Z" fill="white" />
              </g>
              <defs>
                <clipPath id="clip0_278_158">
                  <rect width="21" height="21" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <ReactTooltip place="bottom" type="dark" effect='float' />
          </>
        )}

        <Modal
          isOpen={!!isViewTalkingPoints}
          onBackgroundClick={() => setIsViewTalkingPoints(false)}
          onEscapeKeydown={() => setIsViewTalkingPoints(false)}>
          <Flex
            flexDirection={'column'}
            paddingX={[0, '24px', '24px']}
            style={{
              margin: '0 auto',
              alignSelf: 'center',
              borderRadius: '20px',
              position: 'relative',
              backgroundColor: 'white',
              paddingBottom: '24px',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '96%',
              width: '580px',
              maxWidth: '96%',
              overflow: 'hidden',
              paddingTop: '4px',
              paddingRight: 0
            }}>
            <Flex justifyContent='flex-end' style={{ borderBottom: '1px solid rgb(229, 233, 236)' }}>
              <Text mb={1} mt={1} mr={2} style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setIsViewTalkingPoints(false)}>Close</Text>
            </Flex>
            <Flex style={{ overflow: 'auto', width: '100%', height: '100%' }}>
              <TalkingPoints athenaId={upcoming.patient.patientid || ''} patientName={patientName} />
            </Flex>
          </Flex>
        </Modal>

        <Modal
          isOpen={!!isViewPatient}
          onBackgroundClick={() => setIsViewPatient(false)}
          onEscapeKeydown={() => setIsViewPatient(false)}>
          <Flex
            flexDirection={'column'}
            paddingX={[0, '24px', '24px']}
            style={{
              margin: '0 auto',
              alignSelf: 'center',
              borderRadius: '20px',
              position: 'relative',
              backgroundColor: '#F2F5F9',
              paddingBottom: '24px',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '96',
              height: '96%',
              width: '96%',
              maxWidth: '96%',
              overflow: 'hidden',
              paddingTop: '4px',
              paddingRight: 0
            }}>
            <Flex justifyContent='flex-end' style={{ borderBottom: '1px solid rgb(229, 233, 236)' }}>
              <Text mb={1} mt={1} mr={2} style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setIsViewPatient(false)}>Close</Text>
            </Flex>
            <Flex style={{ overflow: 'scroll', width: '100%', height: '100%' }}>
              <PatientInner id={upcoming.patient.patientid || ''} />

            </Flex>
          </Flex>
        </Modal>
        <Modal
          isOpen={!!isSendSms}
          onBackgroundClick={() => setisSendSms(false)}
          onEscapeKeydown={() => setisSendSms(false)}>
          <SendSms id={upcoming.patientId} name={fullPatientName} close={() => { setisSendSms(false) }} />
        </Modal>
        {!!isViewHpis && upcoming.patient.patientid && (
          <Modal
            isOpen={true}
            onBackgroundClick={() => {
              setIsViewHpis(false);
            }}
            onEscapeKeydown={() => {
              setIsViewHpis(false);
            }}
          >
            <SpecialModalBackground
              onClick={() => {
                setIsViewHpis(false);
              }}
            >
              <Box
                style={{
                  margin: '0 auto',
                  alignSelf: 'center',
                  borderRadius: '20px',
                  position: 'relative',
                  background: 'linear-gradient(150deg, #FDF5F2 0.64%, #D0E1F4 78.42%)',
                  // padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '95vw',
                  minWidth: '95vw',
                  maxHeight: '95vh',
                  minHeight: '95vh',
                  overflowY: 'hidden',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {/* <Wrapper flex={1} flexDirection={'column'} style={{ overflowY: 'auto' }}> */}
                <HpisWrapper patientId={upcoming.patient.patientid} onClose={() => setIsViewHpis(false)} />
                {/* </Wrapper> */}
              </Box>
              {/* <Box
                style={{
                  margin: '0 auto',
                  alignSelf: 'center',
                  borderRadius: '20px',
                  position: 'relative',
                  background: 'white',
                  padding: '24px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  maxWidth: '95vw',
                  minWidth: 'initial',
                  maxHeight: '95vh',
                  minHeight: 'initial',
                  overflowY: 'hidden',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Wrapper flex={1} flexDirection={'column'} style={{ overflowY: 'auto' }}>
                  <ViewHpis athenaId={upcoming.patient.patientid} onClose={() => setIsViewHpis(false)} />
                </Wrapper>
              </Box> */}
            </SpecialModalBackground>
          </Modal>
        )}
        <ReactTooltip id='tooltipGenerateHpi' place="bottom" type="dark" effect="solid" />
      </Card>
    </Flex>
  )
}

const OpenSlotCard = ({
  upcoming
}: { upcoming: IUpcoming }) => {
  return (
    <Flex width='100%' opacity={0.5}>
      <Card style={{ flex: 'initial', padding: '12px 12px', position: 'relative', overflow: 'hidden', width: '100%' }}>
        <Flex flexDirection={'row'} alignItems='center'>
          <Text fontWeight={600} fontSize='18px' flex={1}>{`Open slot`}</Text>
          <Text fontWeight={400} fontSize='11px' color='rgba(52, 79, 122, 0.6)' ml={2}>{`${moment(upcoming.dateStart).format('hh:mmA')} - ${moment(upcoming.dateEnd).format('hh:mmA')}`}</Text>
        </Flex>
      </Card>
    </Flex>
  )
}

const UpcomingCardLoading = () => {
  return (
    <Card style={{ flex: 'initial', padding: '12px 12px' }}>
      <Flex flexDirection={'column'}>
        <Skeleton style={{
          height: '24px',
          width: '72px'
        }} />

        <Text fontWeight={600} fontSize='18px' mb={2} mt={2}><Skeleton style={{ width: '146px' }} /></Text>
        <Flex style={{ width: '100%' }}>

          <Skeleton style={{ width: '124px', height: '16px' }} />

          <Skeleton style={{ width: '124px', marginLeft: '12px' }} />
        </Flex>
        <Flex>
          <svg style={{ opacity: 0, marginRight: '8px' }} width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.1078 3.14727L10.6518 1.69258C10.549 1.58942 10.427 1.50756 10.2925 1.45172C10.1581 1.39588 10.0139 1.36715 9.86836 1.36719C9.57168 1.36719 9.29278 1.4834 9.0836 1.69258L7.5168 3.25937C7.41364 3.36209 7.33178 3.48417 7.27594 3.61861C7.2201 3.75305 7.19137 3.8972 7.19141 4.04277C7.19141 4.33945 7.30762 4.61836 7.5168 4.82754L8.6625 5.97324C8.39432 6.56436 8.02146 7.10211 7.56192 7.56055C7.10353 8.02121 6.56582 8.39542 5.97461 8.66523L4.82891 7.51953C4.7262 7.41637 4.60411 7.33452 4.46967 7.27867C4.33523 7.22283 4.19108 7.19411 4.04551 7.19414C3.74883 7.19414 3.46992 7.31035 3.26074 7.51953L1.69258 9.08496C1.58929 9.18786 1.50737 9.31017 1.45152 9.44485C1.39568 9.57953 1.36702 9.72393 1.36719 9.86973C1.36719 10.1664 1.4834 10.4453 1.69258 10.6545L3.1459 12.1078C3.47949 12.4428 3.94024 12.6328 4.41328 12.6328C4.51309 12.6328 4.60879 12.6246 4.70313 12.6082C6.5461 12.3047 8.37403 11.3244 9.84922 9.85059C11.3231 8.37813 12.302 6.55156 12.6096 4.70312C12.7025 4.13848 12.5152 3.55742 12.1078 3.14727Z" fill="#344F7A" fill-opacity="0.65" />
          </svg>
          <Skeleton style={{ width: '86px' }} />
        </Flex>
        <Flex style={{ width: '100%' }}>
          <Skeleton style={{ marginRight: '8px', width: '130px', height: '27px' }} />
          <Skeleton style={{ marginLeft: '8px', width: '130px', height: '27px' }} />
        </Flex>
      </Flex>
    </Card>)
}

export const UpcomingsWithSession: React.FC<UpcomingsProps & { session: IUser }> = ({ data, loading, error, refetch, session, selectedDate, setSelectedDate, hidePastSelector = false }) => {
  const [hidePast, setHidePast] = useState(selectedDate === moment().format('MM/DD/YYYY'))
  const [showCalendar, setShowCalendar] = useState(false)

  useEffect(() => {
    setHidePast(selectedDate === moment().format('MM/DD/YYYY'))
  }, [selectedDate])

  return (
    <Flex flexDirection={'column'} mr={3} minWidth="300px">
      <Flex alignItems={'center'}>
        {!!setSelectedDate && (
          <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={() => setShowCalendar(true)} style={{ marginRight: '12px', cursor: 'pointer' }}>
            <path opacity="0.2" d="M17.25 3.5V7.25H0.75V3.5C0.75 3.30109 0.829018 3.11032 0.96967 2.96967C1.11032 2.82902 1.30109 2.75 1.5 2.75H16.5C16.6989 2.75 16.8897 2.82902 17.0303 2.96967C17.171 3.11032 17.25 3.30109 17.25 3.5Z" fill="#C51C1C" />
            <path d="M16.5 2H14.25V1.25C14.25 1.05109 14.171 0.860322 14.0303 0.71967C13.8897 0.579018 13.6989 0.5 13.5 0.5C13.3011 0.5 13.1103 0.579018 12.9697 0.71967C12.829 0.860322 12.75 1.05109 12.75 1.25V2H5.25V1.25C5.25 1.05109 5.17098 0.860322 5.03033 0.71967C4.88968 0.579018 4.69891 0.5 4.5 0.5C4.30109 0.5 4.11032 0.579018 3.96967 0.71967C3.82902 0.860322 3.75 1.05109 3.75 1.25V2H1.5C1.10218 2 0.720644 2.15804 0.43934 2.43934C0.158035 2.72064 0 3.10218 0 3.5V18.5C0 18.8978 0.158035 19.2794 0.43934 19.5607C0.720644 19.842 1.10218 20 1.5 20H16.5C16.8978 20 17.2794 19.842 17.5607 19.5607C17.842 19.2794 18 18.8978 18 18.5V3.5C18 3.10218 17.842 2.72064 17.5607 2.43934C17.2794 2.15804 16.8978 2 16.5 2ZM3.75 3.5V4.25C3.75 4.44891 3.82902 4.63968 3.96967 4.78033C4.11032 4.92098 4.30109 5 4.5 5C4.69891 5 4.88968 4.92098 5.03033 4.78033C5.17098 4.63968 5.25 4.44891 5.25 4.25V3.5H12.75V4.25C12.75 4.44891 12.829 4.63968 12.9697 4.78033C13.1103 4.92098 13.3011 5 13.5 5C13.6989 5 13.8897 4.92098 14.0303 4.78033C14.171 4.63968 14.25 4.44891 14.25 4.25V3.5H16.5V6.5H1.5V3.5H3.75ZM16.5 18.5H1.5V8H16.5V18.5ZM7.5 10.25V16.25C7.5 16.4489 7.42098 16.6397 7.28033 16.7803C7.13968 16.921 6.94891 17 6.75 17C6.55109 17 6.36032 16.921 6.21967 16.7803C6.07902 16.6397 6 16.4489 6 16.25V11.4631L5.58563 11.6713C5.4076 11.7603 5.2015 11.7749 5.01268 11.712C4.82385 11.649 4.66776 11.5137 4.57875 11.3356C4.48974 11.1576 4.47509 10.9515 4.53803 10.7627C4.60097 10.5739 4.73635 10.4178 4.91437 10.3287L6.41437 9.57875C6.52876 9.52151 6.65589 9.49448 6.78367 9.50022C6.91145 9.50596 7.03563 9.54429 7.14442 9.61155C7.25322 9.67882 7.343 9.77279 7.40523 9.88454C7.46747 9.99629 7.50009 10.1221 7.5 10.25ZM13.0463 13.1047L11.25 15.5H12.75C12.9489 15.5 13.1397 15.579 13.2803 15.7197C13.421 15.8603 13.5 16.0511 13.5 16.25C13.5 16.4489 13.421 16.6397 13.2803 16.7803C13.1397 16.921 12.9489 17 12.75 17H9.75C9.61072 17 9.47418 16.9612 9.3557 16.888C9.23722 16.8148 9.14147 16.71 9.07918 16.5854C9.01689 16.4608 8.99052 16.3214 9.00303 16.1826C9.01554 16.0439 9.06643 15.9114 9.15 15.8L11.8481 12.2028C11.9095 12.1211 11.9535 12.0277 11.9775 11.9284C12.0015 11.8291 12.0049 11.7259 11.9876 11.6252C11.9703 11.5245 11.9325 11.4284 11.8767 11.3428C11.8209 11.2572 11.7482 11.1839 11.6631 11.1274C11.5779 11.0709 11.4821 11.0324 11.3816 11.0143C11.281 10.9961 11.1778 10.9987 11.0783 11.0219C10.9788 11.0451 10.885 11.0884 10.8028 11.1491C10.7206 11.2098 10.6517 11.2867 10.6003 11.375C10.5525 11.463 10.4876 11.5406 10.4093 11.6031C10.3311 11.6656 10.2411 11.7118 10.1447 11.739C10.0483 11.7661 9.94745 11.7737 9.84807 11.7613C9.74869 11.7489 9.65281 11.7166 9.56609 11.6665C9.47936 11.6165 9.40355 11.5495 9.34312 11.4696C9.28269 11.3898 9.23887 11.2986 9.21424 11.2015C9.18962 11.1044 9.18468 11.0034 9.19973 10.9044C9.21478 10.8054 9.24951 10.7104 9.30188 10.625C9.54962 10.1963 9.93188 9.86124 10.3894 9.67184C10.8469 9.48245 11.3541 9.44926 11.8324 9.57743C12.3107 9.7056 12.7333 9.98797 13.0348 10.3808C13.3363 10.7736 13.4998 11.2548 13.5 11.75C13.5016 12.2391 13.3421 12.7152 13.0463 13.1047Z" fill="#344f79" />
          </svg>

        )}
        <Flex flex={1}>
          {!!setSelectedDate && (
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => {
                setSelectedDate(moment(selectedDate).add(-1, 'day').format('MM/DD/YYYY'))
              }}>
              <svg style={{}} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.7099 6.71005C14.6174 6.61734 14.5075 6.5438 14.3865 6.49361C14.2656 6.44343 14.1359 6.4176 14.0049 6.4176C13.874 6.4176 13.7443 6.44343 13.6233 6.49361C13.5023 6.5438 13.3924 6.61734 13.2999 6.71005L8.70992 11.3C8.61722 11.3926 8.54367 11.5024 8.49349 11.6234C8.44331 11.7444 8.41748 11.8741 8.41748 12.005C8.41748 12.136 8.44331 12.2657 8.49349 12.3867C8.54367 12.5076 8.61722 12.6175 8.70992 12.71L13.2999 17.3C13.3925 17.3926 13.5024 17.4661 13.6234 17.5162C13.7443 17.5663 13.874 17.5921 14.0049 17.5921C14.1359 17.5921 14.2655 17.5663 14.3865 17.5162C14.5074 17.4661 14.6173 17.3926 14.7099 17.3C14.8025 17.2075 14.8759 17.0976 14.9261 16.9766C14.9762 16.8556 15.0019 16.726 15.0019 16.595C15.0019 16.4641 14.9762 16.3345 14.9261 16.2135C14.8759 16.0925 14.8025 15.9826 14.7099 15.89L10.8299 12L14.7099 8.12005C15.0999 7.73005 15.0899 7.09005 14.7099 6.71005Z" fill="#344f79" />
              </svg>
            </Box>
          )}

          <Text style={{ fontSize: '16px', fontWeight: 600, userSelect: 'none' }}>{`${moment(selectedDate).format('MMM DD, YYYY')}${moment().isSameOrBefore(moment(selectedDate), 'd') ? '' : ' (Past)'} ${selectedDate === moment().format('MM/DD/YYYY') ? ' (today)' : `${!loading && data?.providerGetUpcomings ? `(${data.providerGetUpcomings.length} visits)` : ''}`}`}</Text>

          {!!setSelectedDate && (
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              onClick={() => {
                setSelectedDate(moment(selectedDate).add(1, 'day').format('MM/DD/YYYY'))
              }}>
              <svg style={{ transform: 'rotate(180deg)' }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.7099 6.71005C14.6174 6.61734 14.5075 6.5438 14.3865 6.49361C14.2656 6.44343 14.1359 6.4176 14.0049 6.4176C13.874 6.4176 13.7443 6.44343 13.6233 6.49361C13.5023 6.5438 13.3924 6.61734 13.2999 6.71005L8.70992 11.3C8.61722 11.3926 8.54367 11.5024 8.49349 11.6234C8.44331 11.7444 8.41748 11.8741 8.41748 12.005C8.41748 12.136 8.44331 12.2657 8.49349 12.3867C8.54367 12.5076 8.61722 12.6175 8.70992 12.71L13.2999 17.3C13.3925 17.3926 13.5024 17.4661 13.6234 17.5162C13.7443 17.5663 13.874 17.5921 14.0049 17.5921C14.1359 17.5921 14.2655 17.5663 14.3865 17.5162C14.5074 17.4661 14.6173 17.3926 14.7099 17.3C14.8025 17.2075 14.8759 17.0976 14.9261 16.9766C14.9762 16.8556 15.0019 16.726 15.0019 16.595C15.0019 16.4641 14.9762 16.3345 14.9261 16.2135C14.8759 16.0925 14.8025 15.9826 14.7099 15.89L10.8299 12L14.7099 8.12005C15.0999 7.73005 15.0899 7.09005 14.7099 6.71005Z" fill="#344f79" />
              </svg>
            </Box>
          )}
        </Flex>

        <ToolbarMenuButton data-tip='Refresh upcoming' onClick={refetch} background='transparent'>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 11H7.101L7.102 10.991C7.23257 10.3516 7.48813 9.74434 7.854 9.20399C8.39845 8.4018 9.16215 7.77315 10.054 7.39299C10.356 7.26499 10.671 7.16699 10.992 7.10199C11.6579 6.96698 12.3441 6.96698 13.01 7.10199C13.967 7.29808 14.8451 7.7714 15.535 8.46299L16.951 7.05099C16.3128 6.41262 15.5578 5.90303 14.727 5.54999C14.3033 5.37062 13.8628 5.23394 13.412 5.14199C12.4818 4.953 11.5232 4.953 10.593 5.14199C10.1419 5.23432 9.70101 5.37133 9.277 5.55099C8.02753 6.08109 6.95793 6.96108 6.197 8.08499C5.68489 8.84284 5.32676 9.69398 5.143 10.59C5.115 10.725 5.1 10.863 5.08 11H2L6 15L10 11ZM14 13H16.899L16.898 13.008C16.6367 14.2897 15.8812 15.4171 14.795 16.146C14.2548 16.5122 13.6475 16.7677 13.008 16.898C12.3424 17.033 11.6566 17.033 10.991 16.898C10.3516 16.7674 9.74435 16.5119 9.204 16.146C8.93862 15.9665 8.69085 15.7622 8.464 15.536L7.05 16.95C7.68851 17.5882 8.44392 18.0974 9.275 18.45C9.699 18.63 10.142 18.767 10.59 18.858C11.5198 19.0471 12.4782 19.0471 13.408 18.858C15.2005 18.4859 16.7773 17.4294 17.803 15.913C18.3146 15.1557 18.6724 14.3053 18.856 13.41C18.883 13.275 18.899 13.137 18.919 13H22L18 8.99999L14 13Z" fill="#344f79" />
          </svg>
        </ToolbarMenuButton>
      </Flex>

      {(!!loading) && (
        [1, 2, 3].map(x => {
          return <UpcomingCardLoading key={`loadingUpcomings${x}`} />
        })
      )}

      {!loading && data?.providerGetUpcomings && data?.providerGetUpcomings.length > 0 &&
        (
          <VerticalTimeline layout='1-column' lineColor='#344F7A' >
            {(!selectedDate || selectedDate === moment().format('MM/DD/YYYY')) && !hidePastSelector && (
              <VerticalTimelineElementCustomFilter
                className="vertical-timeline-element--work"
                contentStyle={contentStyle}
                contentArrowStyle={contentArrowStyle}
                iconStyle={iconStyle}
              >
                <Flex style={{ width: '100%', border: '1px solid #DFDFDF', borderRadius: '10px', padding: '4px', alignItems: 'center' }}>
                  <Text flex={1} fontSize='12px'>Past visits</Text>
                  <Text style={{ fontSize: '12px' }} mr={2}>Show</Text>
                  <Toggle
                    checked={hidePast}
                    // defaultChecked={this.state.tofuIsReady}
                    icons={false}
                    onChange={(e) => setHidePast(e.target.checked)}
                  />
                  <Text style={{ fontSize: '12px' }} ml={2}>Hide</Text>
                </Flex>
              </VerticalTimelineElementCustomFilter>
            )}
            {data?.providerGetUpcomings.filter(x => {
              if (selectedDate !== moment().format('MM/DD/YYYY')) return true

              if (!!hidePast) {
                if (moment(x.dateEnd).isBefore(moment())) return false
              }
              return true
            }).map(x => {
              return (
                <VerticalTimelineElementCustom
                  className="vertical-timeline-element--work"
                  contentStyle={contentStyle}
                  contentArrowStyle={contentArrowStyle}
                  iconStyle={iconStyle}

                  key={!!x.isSlot ? `slt${x.id}` : `aid${x.appointmentId}`}
                >
                  {!!x.isSlot ? <OpenSlotCard upcoming={x} /> :
                    <UpcomingCard upcoming={x} session={session} />}
                </VerticalTimelineElementCustom>)
            })}
          </VerticalTimeline>
        )}
      {/* {data?.providerGetUpcomings.map(x => (
        <UpcomingCard key={x.appointmentId} upcoming={x} session={session} />
      ))} */}
      {!loading && ((!data || !data.providerGetUpcomings || data.providerGetUpcomings.length < 1) || data?.providerGetUpcomings.filter(x => {
        if (selectedDate !== moment().format('MM/DD/YYYY')) return true

        if (!!hidePast) {
          if (moment(x.dateEnd).isBefore(moment())) return false
        }
        return true
      }).length < 1) && (
          <Flex flexDirection={'column'} width={'100%'} justifyContent='flex-start' alignItems={'center'}>
            <NoUpcomings style={{ maxWidth: '150px', maxHeight: '207.6px' }} />
            <Text fontWeight={300} textAlign='center'>No {moment().isSameOrBefore(moment(selectedDate), 'd') ? 'upcoming' : 'past'} appointments or slots<br />on {moment(selectedDate).format('MMM DD, YYYY')}</Text>
          </Flex>
        )}
      {!!showCalendar && !!setSelectedDate && (

        <Modal
          isOpen={!!showCalendar}
          onBackgroundClick={() => setShowCalendar(false)}
          onEscapeKeydown={() => setShowCalendar(false)}>
          <MinimalCalendarGrid
            showTitle={false} selectedDate={selectedDate || null} setSelectedDate={(x) => {
              setShowCalendar(false)
              setSelectedDate(x)
            }} />
        </Modal>
      )}
      <ReactTooltip place="bottom" type="dark" effect='float' />
    </Flex>);
}

export const Upcomings: React.FC<UpcomingsProps> = withSession(UpcomingsWithSession)